package io.jsonize.web.worker

import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.random.Random

// Minimal external Worker binding (avoids depending on exact DOM package names)
external class Worker(scriptURL: String) {
    fun postMessage(message: Any?)
    var onmessage: ((dynamic) -> Unit)?
    fun terminate()
}

data class HighlightSpan(
    val start: Int,
    val end: Int,
    val kind: String
)

data class JsonValidationResult(
    val valid: Boolean,
    val message: String? = null,
    val offset: Int? = null,
    val line: Int? = null,
    val column: Int? = null
)

/**
 * Singleton client to talk to jsonWorker.js
 *
 * Operations:
 *  - format(text)   → pretty JSON (via JSON.parse + JSON.stringify with 2 spaces)
 *  - minify(text)   → compact JSON
 *  - validate(text) → throws if invalid
 *  - tokenize(text) → List<HighlightSpan> for syntax highlighting
 */
object JsonWorkerClient {

    // Adjust path if your worker is served differently (e.g. "./jsonWorker.js")
    private val worker: Worker by lazy { Worker("jsonWorker.js").also(::setupListener) }

    private val pending = mutableMapOf<String, CompletableDeferred<dynamic>>()
    private val mutex = Mutex()

    private fun setupListener(w: Worker) {
        w.onmessage = { event ->
            val data = event.data
            val id = data.id as String
            val ok = data.ok as Boolean
            var def: CompletableDeferred<dynamic>?

            // remove from pending under lock
            kotlinx.coroutines.GlobalScope.launch {
                mutex.withLock {
                    def = pending.remove(id)
                }
                if (def != null) {
                    if (ok) {
                        def?.complete(data)
                    } else {
                        val err = data.error as? String ?: "Worker error"
                        def?.completeExceptionally(Throwable(err))
                    }
                }
            }
        }
    }

    private suspend fun <T> call(op: String, text: String, mapper: (dynamic) -> T): T {
        val id = Random.nextLong().toString()
        val deferred = CompletableDeferred<dynamic>()

        mutex.withLock {
            pending[id] = deferred
        }

        val msg = js("({ id: id, op: op, text: text })")
        worker.postMessage(msg)

        val resp = deferred.await()
        return mapper(resp)
    }

    suspend fun format(text: String): String =
        call("format", text) { data -> data.result as String }

    suspend fun minify(text: String): String =
        call("minify", text) { data -> data.result as String }

    suspend fun tokenize(text: String): List<HighlightSpan> =
        call("tokenize", text) { data ->
            val arr = data.result as Array<dynamic>
            arr.map {
                HighlightSpan(
                    start = it.start as Int,
                    end = it.end as Int,
                    kind = it.kind as String
                )
            }
        }

    suspend fun validate(text: String): JsonValidationResult =
        call("validate", text) { data ->
            val result = data.result
            val valid = (result.valid as Boolean?) ?: false
            if (valid) {
                JsonValidationResult(valid = true)
            } else {
                JsonValidationResult(
                    valid = false,
                    message = result.message as String?,
                    offset = (result.offset as Int?),
                    line = (result.line as Int?),
                    column = (result.column as Int?)
                )
            }
        }
}
